import { Directive, ElementRef, HostListener, Renderer2, forwardRef, OnInit } from '@angular/core'
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms'

@Directive({
  selector: '[appIndianNumberInputFormat]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IndianNumberInputFormatDirective),
      multi: true
    }
  ]
})
export class IndianNumberInputFormatDirective implements ControlValueAccessor, OnInit {
  private lastValue: number | null = null

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: number | null) => void = () => {}
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {}

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.renderer.setAttribute(this.el.nativeElement, 'type', 'text')
    this.renderer.setAttribute(this.el.nativeElement, 'inputmode', 'numeric')
  }

  @HostListener('input', ['$event.target'])
  onInput(input: HTMLInputElement): void {
    const originalValue = input.value
    const cursorPosition = input.selectionStart ?? 0
    const numericString = originalValue.replace(/\D/g, '')

    let rawValue: number | null = null
    let formattedValue = ''

    if (numericString === '') {
      rawValue = null
      formattedValue = ''
    } else {
      const parsedValue = parseInt(numericString, 10)
      if (!isNaN(parsedValue)) {
        rawValue = parsedValue
        formattedValue = this.formatIndianCurrency(rawValue)
      } else {
        // Handle potential parsing errors if needed, though unlikely with \D removal
        rawValue = null
        formattedValue = ''
      }
    }

    if (rawValue !== this.lastValue) {
      this.lastValue = rawValue
      this.renderer.setProperty(input, 'value', formattedValue)
      this.onChange(rawValue)

      // --- Cursor Adjustment ---
      // Calculate how many non-digit characters were added/removed *before* the cursor's logical position.
      const originalDigitsBeforeCursor = originalValue
        .substring(0, cursorPosition)
        .replace(/\D/g, '').length

      let newCursorPos = 0
      let digitsCounted = 0
      // Find the position in the *new* formatted string corresponding to the digit count
      for (
        let i = 0;
        i < formattedValue.length && digitsCounted < originalDigitsBeforeCursor;
        i++
      ) {
        if (/\d/.test(formattedValue[i])) {
          digitsCounted++
        }
        newCursorPos++
      }
      // Adjust cursor to be after the counted digits, potentially skipping commas
      while (
        newCursorPos < formattedValue.length &&
        digitsCounted === originalDigitsBeforeCursor &&
        !/\d/.test(formattedValue[newCursorPos])
      ) {
        newCursorPos++
      }

      // Ensure cursor position is valid
      newCursorPos = Math.max(0, Math.min(formattedValue.length, newCursorPos))

      // Set the cursor position asynchronously
      requestAnimationFrame(() => {
        // Check if element still has focus to avoid errors if blurred quickly
        if (document.activeElement === input) {
          input.setSelectionRange(newCursorPos, newCursorPos)
        }
      })
      // --- End Cursor Adjustment ---
    } else if (input.value !== formattedValue) {
      // If only the display needs correcting (e.g., pasting formatted text)
      // but the raw value is the same, just update the display and cursor
      const currentCursor = input.selectionStart // Preserve cursor if possible
      this.renderer.setProperty(input, 'value', formattedValue)
      if (currentCursor !== null && document.activeElement === input) {
        input.setSelectionRange(currentCursor, currentCursor)
      }
    }
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched()

    const currentNumericString = this.el.nativeElement.value.replace(/\D/g, '')
    let rawValue: number | null = null

    if (currentNumericString === '') {
      rawValue = null
    } else {
      const parsedValue = parseInt(currentNumericString, 10)
      rawValue = isNaN(parsedValue) ? null : parsedValue
    }

    const formattedValue = this.formatIndianCurrency(rawValue)
    this.renderer.setProperty(this.el.nativeElement, 'value', formattedValue)

    // Ensure the model is updated if blur formatting changes the numeric value
    if (rawValue !== this.lastValue) {
      this.lastValue = rawValue
      this.onChange(rawValue)
    }
  }

  // --- ControlValueAccessor Implementation ---

  writeValue(value: number | null | undefined): void {
    const formattedValue = this.formatIndianCurrency(value)
    this.lastValue = value === undefined || isNaN(Number(value)) ? null : Number(value)
    this.renderer.setProperty(this.el.nativeElement, 'value', formattedValue)
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState?(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled)
  }

  // --- Helper Methods ---
  private formatIndianCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined || isNaN(value)) {
      return ''
    }
    // Use Number.MAX_SAFE_INTEGER to prevent issues with extremely large numbers if needed
    const safeValue = Math.min(value, Number.MAX_SAFE_INTEGER)
    return safeValue.toLocaleString('en-IN', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    })
  }
}
